import { authenticatedFetch } from '../JWTAuthService';
import { configuration } from '../Configuration';

/**
 * The Zensus grid metrics served by tri-server (`GET /metrics/catalog`, `POST /metrics/tiles`).
 *
 * The server decides the colour scale and hands back cells that are already coloured, so nothing
 * here interprets a metric — this module only carries the request there and the drawing back.
 */

const baseUrl = configuration.serverUrl;

/** A theme drawn as a whole rather than by one of its categories. */
export const TOTAL_CATEGORY = '__total__';

export interface MetricCategory {
    key: string;
    label: string;
}

export interface MetricTheme {
    theme: string;
    label: string;
    year: number;
    /** A breakdown has categories; a measure has a single value per cell. */
    kind: 'breakdown' | 'measure';
    categories: MetricCategory[];
}

export interface ScaleCategory {
    label: string;
    color: string;
    min: number;
    max: number;
}

export interface TileResult {
    theme: string;
    themeLabel: string;
    category: string;
    categoryLabel: string;
    year: number | null;
    resolutionM: number;
    tileCount: number;
    statistics: { min: number; max: number; mean: number; sum: number } | null;
    scale: ScaleCategory[];
    bounds: [[number, number], [number, number]] | null;
    elements: Array<{ display: Record<string, any> }>;
    /** The area held more cells than the server will return in one go. */
    truncated: boolean;
}

export async function getMetricCatalog(): Promise<MetricTheme[]> {
    const response = await authenticatedFetch(`${baseUrl}/metrics/catalog`, { method: 'GET' });

    if (!response || !response.ok) {
        throw new Error(`Metrikkatalog konnte nicht geladen werden (${response?.status ?? 'keine Antwort'})`);
    }

    const payload = await response.json();
    return payload?.metrics ?? [];
}

/** Worauf ein Score ausgewertet wird. */
export type Granularity = 'cell' | 'parcel' | 'building';
/** Wie Mengen einer Kachel auf Flurstücke verteilt werden: nach Geschossfläche der Gebäude oder nach Fläche. */
export type Distribution = 'buildings' | 'area';

/** Eine Metrik, die ein Score verwenden kann — aus allen Quellen der Heatmap (Server: scoring/catalog.ts). */
export interface ScoringMetric {
    id: string;
    label: { de: string; en: string };
    unit: string | null;
    basis: 'cell' | 'grid1km' | 'region' | 'area' | 'building' | 'parcel' | 'point';
    kind: 'extensive' | 'intensive' | 'share' | 'distance' | 'count';
    source:
        | { kind: 'zensus'; theme: string; category: string }
        | { kind: 'inkar'; type: string; field: string }
        | { kind: 'layer'; group: 'climate' | 'hazards' | 'nature' | 'layers' }
        | { kind: 'poi'; className: string };
    group?: { de: string; en: string };
}

export interface ScoreResult extends TileResult {
    granularity: Granularity;
    distribution: Distribution;
    coverage: { name: string; label: string; withValue: number; units: number }[];
    notes: string[];
    /** Nur an Liegenschaften gerechnet: je Liegenschaft ihr Score und dessen Farbe. */
    places?: { id: string; unitId: string | null; value: number | null; color: string | null }[];
}

export async function getScoringCatalog(): Promise<ScoringMetric[]> {
    const response = await authenticatedFetch(`${baseUrl}/metrics/score-catalog`, { method: 'GET' });
    if (!response || !response.ok) {
        throw new Error(`Metrikkatalog konnte nicht geladen werden (${response?.status ?? 'keine Antwort'})`);
    }
    const payload = await response.json();
    return payload?.metrics ?? [];
}

/** Ein Score auf Kacheln, Flurstücken oder Gebäuden des Gebiets. */
export async function getScore(request: {
    /** Über einem Gebiet — oder `places`: nur an diesen Liegenschaften, dann ohne Gebiet. */
    geometry?: any;
    places?: { id: string; lat: number; lon: number }[];
    formula: string;
    name?: string;
    parameters?: Record<string, number>;
    granularity: Granularity;
    distribution: Distribution;
}, signal?: AbortSignal): Promise<ScoreResult> {
    // Mit `signal`: eine überholte Anfrage wird abgebrochen, und der Server hört auf zu rechnen.
    const response = await authenticatedFetch(`${baseUrl}/metrics/score`, {
        method: 'POST',
        signal,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
    });
    if (!response || !response.ok) {
        const detail = await response?.json().catch(() => null);
        throw new Error(detail?.error ?? `Score konnte nicht berechnet werden (${response?.status ?? 'keine Antwort'})`);
    }
    return response.json();
}

/** Die gespeicherten Scores des Mandanten. */
export async function listSavedScores<T>(): Promise<T[]> {
    const response = await authenticatedFetch(`${baseUrl}/metrics/scores`, { method: 'GET' });
    if (!response || !response.ok) throw new Error(`Scores konnten nicht geladen werden (${response?.status ?? 'keine Antwort'})`);
    return (await response.json())?.scores ?? [];
}

export async function putSavedScore<T extends { id: string }>(score: T): Promise<T> {
    const response = await authenticatedFetch(`${baseUrl}/metrics/scores/${encodeURIComponent(score.id)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(score),
    });
    if (!response || !response.ok) throw new Error(`Score konnte nicht gespeichert werden (${response?.status ?? 'keine Antwort'})`);
    return response.json();
}

export async function deleteSavedScore(id: string): Promise<void> {
    const response = await authenticatedFetch(`${baseUrl}/metrics/scores/${encodeURIComponent(id)}`, { method: 'DELETE' });
    if (!response || !response.ok) throw new Error(`Score konnte nicht gelöscht werden (${response?.status ?? 'keine Antwort'})`);
}

export async function getMetricTiles(request: {
    geometry: any;
    theme: string;
    category?: string;
    year?: number;
}): Promise<TileResult> {
    const response = await authenticatedFetch(`${baseUrl}/metrics/tiles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
    });

    if (!response || !response.ok) {
        const detail = await response?.json().catch(() => null);
        throw new Error(detail?.error ?? `Kacheln konnten nicht geladen werden (${response?.status ?? 'keine Antwort'})`);
    }

    return response.json();
}
